// import libraries
import { subscribeAuth, signOutUser } from './api';

const SLICE_NAME = 'auth';

export const createAuthSlice = (set, get) => {
  // Internal resources (not in state)
  let unsubscribe = null;
  let expiryTimer = null;

  const clearExpiryTimer = () => {
    if (expiryTimer) {
      try { clearTimeout(expiryTimer); } catch (_) {}
      expiryTimer = null;
    }
  };

  const scheduleExpiry = (expiresAt) => {
    clearExpiryTimer();
    if (!expiresAt) return;
    const delay = Math.max(0, expiresAt - Date.now());
    expiryTimer = setTimeout(() => {
      // Mark expired immediately; if Firebase refreshes token, onIdTokenChanged will flip it back
      set((state) => ({
        common: {
          ...state.common,
          [SLICE_NAME]: {
            ...state.common?.[SLICE_NAME],
            isExpired: true,
            isAuthenticated: false,
          },
        },
      }));
    }, delay);
  };

  const applySession = (session) => {
    const expiresAt = session?.expiresAt || null;
    const isExpired = expiresAt ? Date.now() >= expiresAt : false;
    const isAuthenticated = !!session && !isExpired;

    set((state) => ({
      common: {
        ...state.common,
        [SLICE_NAME]: {
          ...(state.common?.[SLICE_NAME] || {}),
          session: session || null,
          expiresAt,
          isExpired,
          isAuthenticated,
          isLoading: false,
          error: null,
        },
      },
    }));

    // Handle timer for expiration boundaries
    if (session && expiresAt) {
      scheduleExpiry(expiresAt);
    } else {
      clearExpiryTimer();
    }
  };

  return {
    [SLICE_NAME]: {
      session: null,
      isAuthenticated: false,
      isExpired: false,
      expiresAt: null,
      isLoading: false,
      error: null,

      initAuthListener: () => {
        // idempotent
        if (unsubscribe) return unsubscribe;
        set((state) => ({
          common: {
            ...state.common,
            [SLICE_NAME]: { ...(state.common?.[SLICE_NAME] || {}), isLoading: true, error: null },
          },
        }));
        unsubscribe = subscribeAuth(
          (session) => {
            applySession(session);
          },
          (error) => {
            set((state) => ({
              common: {
                ...state.common,
                [SLICE_NAME]: { ...(state.common?.[SLICE_NAME] || {}), isLoading: false, error },
              },
            }));
          }
        );
        return unsubscribe;
      },

      teardownAuthListener: () => {
        if (unsubscribe) {
          try { unsubscribe(); } catch (_) {}
          unsubscribe = null;
        }
        clearExpiryTimer();
      },

      signOut: async () => {
        await signOutUser();
      },

      // helpers
      getSession: () => get().common?.[SLICE_NAME]?.session || null,
    },
  };
};

// Selectors
export const selectAuthSlice = (state) => state.common?.[SLICE_NAME] || state[SLICE_NAME];
export const selectIsAuthenticated = (state) => !!(state.common?.[SLICE_NAME]?.isAuthenticated);
export const selectAuthSession = (state) => state.common?.[SLICE_NAME]?.session || null;